import { Helmet } from "react-helmet-async";
import { useSiteSettings } from "@/lib/siteSettings";

const DEFAULT_PUBLIC_ORIGIN = process.env.REACT_APP_PUBLIC_ORIGIN || "https://ivyleagueconsulting.org";
const SITE_NAME = "Beyond Valencia";
const TITLE_SUFFIX = " — Beyond Valencia";
const DEFAULT_DESCRIPTION = "Florida transfer admissions strategy from Valencia College to UF, UCF, FSU, Harvard, and America's most selective universities. Founded by Taimur Ahmed, Harvard ALM '17 — a former community college transfer student.";
const DEFAULT_OG_IMAGE = "/og-default.png";
const DEFAULT_TWITTER_HANDLE = "@ivyleaguecons";

const ORG_JSONLD = {
  "@context": "https://schema.org",
  "@type": "EducationalOrganization",
  name: SITE_NAME,
  alternateName: "Beyond Valencia by Ivy League Consulting",
  url: DEFAULT_PUBLIC_ORIGIN,
  description: DEFAULT_DESCRIPTION,
  founder: { "@type": "Person", name: "Taimur Ahmed", sameAs: [`${DEFAULT_PUBLIC_ORIGIN}/about`] },
  address: { "@type": "PostalAddress", addressLocality: "Orlando", addressRegion: "FL", addressCountry: "US" },
  areaServed: [
    { "@type": "State", name: "Florida" },
    { "@type": "Country", name: "United States" },
  ],
  sameAs: [],
};

const WEBSITE_JSONLD = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  url: DEFAULT_PUBLIC_ORIGIN,
  name: SITE_NAME,
};

function truncate(str, max) {
  if (!str) return str;
  if (str.length <= max) return str;
  return str.slice(0, max - 1).trimEnd() + "\u2026";
}

/**
 * SEO — reusable meta component. Renders <title>, description, canonical,
 * OpenGraph, Twitter card, JSON-LD (site-wide + per-page + optional breadcrumbs),
 * and Google Search Console / Bing verification meta tags from site_settings.
 */
export default function SEO({
  title,
  description,
  canonical = "/",
  ogImage,
  ogType = "website",
  jsonLd,
  noindex = false,
  preloadImage,
}) {
  const settings = useSiteSettings();
  const origin = (settings && settings.site_url) || DEFAULT_PUBLIC_ORIGIN;
  const twitterHandle = (settings && settings.twitter_handle) || DEFAULT_TWITTER_HANDLE;

  const rawTitle = title ? (title.includes(SITE_NAME) ? title : title + TITLE_SUFFIX) : SITE_NAME;
  const fullTitle = truncate(rawTitle, 60);
  const desc = truncate(description || DEFAULT_DESCRIPTION, 160);
  const url = origin.replace(/\/$/, "") + (canonical.startsWith("/") ? canonical : "/" + canonical);
  const settingsOgImage = settings && settings.default_og_image_url;
  const chosenOg = ogImage || settingsOgImage || DEFAULT_OG_IMAGE;
  const image = chosenOg.startsWith("http") ? chosenOg : origin.replace(/\/$/, "") + chosenOg;

  const perPageLd = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];
  const orgWithSameAs = { ...ORG_JSONLD, url: origin, logo: `${origin.replace(/\/$/, "")}/logo.png` };
  const websiteLd = { ...WEBSITE_JSONLD, url: origin };
  const allLd = [orgWithSameAs, websiteLd, ...perPageLd];

  const gsc = settings && settings.google_search_console_verification;
  const bing = settings && settings.bing_webmaster_verification;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <link rel="canonical" href={url} />
      {noindex ? (
        <meta name="robots" content="noindex,nofollow" />
      ) : (
        <meta name="robots" content="index,follow" />
      )}
      {gsc && <meta name="google-site-verification" content={gsc} />}
      {bing && <meta name="msvalidate.01" content={bing} />}

      {/* Open Graph */}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={image} />
      <meta property="og:locale" content="en_US" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={twitterHandle} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={image} />

      {preloadImage && <link rel="preload" as="image" href={preloadImage} />}

      {allLd.map((obj, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(obj)}</script>
      ))}
    </Helmet>
  );
}

export { DEFAULT_PUBLIC_ORIGIN, SITE_NAME };
